import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  setCurrentMonth,
  toggleSelectedDate,
  clearSelectedDates,
  setReason,
  addEvent,
  fetchLeaveRequests,
  submitLeaveRequest,
  approveLeaveRequest,
} from "../../../state/reducers/calendarSlice";
import "tailwindcss/tailwind.css";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import toast from "react-hot-toast";

dayjs.extend(isBetween);

function CalendarComponent() {
  const dispatch = useDispatch();
  const { calendarState } = useSelector((state) => state.calendar);
  const user = useSelector((state) => state.user);

  const [selectedLeaveRequest, setSelectedLeaveRequest] = useState(null);
  const [leaveRequestsForDate, setLeaveRequestsForDate] = useState([]);
  const [selectedDateForDisplay, setSelectedDateForDisplay] = useState(null);
  const [selectionMode, setSelectionMode] = useState("single"); // "single" or "range"
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);

  // Fetch leave requests when the component mounts
  useEffect(() => {
    dispatch(fetchLeaveRequests());
  }, [dispatch]);

  const currentMonthObj = dayjs(calendarState.currentMonth);
  const daysInMonth = currentMonthObj.daysInMonth();
  const firstDayOfMonth = currentMonthObj.startOf("month").day();
  const lastDayOfMonth = currentMonthObj.endOf("month").day();

  const handlePrevMonth = () =>
    dispatch(
      setCurrentMonth(currentMonthObj.subtract(1, "month").format("YYYY-MM-DD"))
    );

  const handleNextMonth = () =>
    dispatch(
      setCurrentMonth(currentMonthObj.add(1, "month").format("YYYY-MM-DD"))
    );

  // Helper function to generate date range
  const generateDateRange = (startDate, endDate) => {
    const dates = [];
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    let current = start;

    while (current.isBefore(end) || current.isSame(end, "day")) {
      dates.push(current.format("YYYY-MM-DD"));
      current = current.add(1, "day");
    }

    return dates;
  };

  // Helper function to check if date is in preview range
  const isInPreviewRange = (date) => {
    if (!rangeStart || !date) return false;
    if (!rangeEnd) return false;

    const checkDate = dayjs(date);
    const start = dayjs(rangeStart);
    const end = dayjs(rangeEnd);

    return checkDate.isBetween(start, end, "day", "[]");
  };

  const handleDateSelect = (date) => {
    const selectedDate = currentMonthObj.date(date).format("YYYY-MM-DD");
    const currentSelectedDates = calendarState?.selectedDates || [];
    const isCurrentlySelected = currentSelectedDates.includes(selectedDate);

    if (user.role === "superadmin") {
      if (isCurrentlySelected) {
        dispatch(toggleSelectedDate(selectedDate));
        setSelectedDateForDisplay(null);
        setLeaveRequestsForDate([]);
        setSelectedLeaveRequest(null);
      } else {
        dispatch(clearSelectedDates());
        dispatch(toggleSelectedDate(selectedDate));
        setSelectedDateForDisplay(selectedDate);

        const requestsForSelectedDate = (
          calendarState?.leaveRequests || []
        ).filter((req) => {
          const startDate = dayjs(req.start_date);
          const endDate = dayjs(req.end_date);
          return dayjs(selectedDate).isBetween(startDate, endDate, null, "[]");
        });

        setLeaveRequestsForDate(requestsForSelectedDate);
        setSelectedLeaveRequest(
          requestsForSelectedDate.length > 0 ? requestsForSelectedDate[0] : null
        );
      }
    } else if (user.role === "admin") {
      const isLeaveDate = isInLeaveRange(selectedDate);

      if (selectionMode === "range") {
        if (!rangeStart) {
          // First click - set range start
          setRangeStart(selectedDate);
          setRangeEnd(null);
          dispatch(clearSelectedDates());
          dispatch(toggleSelectedDate(selectedDate));
        } else if (!rangeEnd) {
          // Second click - set range end and select all dates in between
          const start = dayjs(rangeStart);
          const end = dayjs(selectedDate);

          // Ensure start is before end
          const actualStart = start.isBefore(end) ? start : end;
          const actualEnd = start.isBefore(end) ? end : start;

          setRangeEnd(actualEnd.format("YYYY-MM-DD"));

          // Generate all dates in range
          const dateRange = generateDateRange(
            actualStart.format("YYYY-MM-DD"),
            actualEnd.format("YYYY-MM-DD")
          );

          // Clear current selection and add all dates in range
          dispatch(clearSelectedDates());
          dateRange.forEach((dateInRange) => {
            dispatch(toggleSelectedDate(dateInRange));
          });

          setSelectedDateForDisplay(selectedDate);
        } else {
          // Third click - start new range
          setRangeStart(selectedDate);
          setRangeEnd(null);
          dispatch(clearSelectedDates());
          dispatch(toggleSelectedDate(selectedDate));
        }
      } else {
        // Single date selection mode
        if (isCurrentlySelected) {
          dispatch(toggleSelectedDate(selectedDate));

          const remainingDates = currentSelectedDates.filter(
            (d) => d !== selectedDate
          );
          if (remainingDates.length > 0) {
            const sortedDates = [...remainingDates].sort(
              (a, b) => dayjs(a).valueOf() - dayjs(b).valueOf()
            );
            setSelectedDateForDisplay(sortedDates[sortedDates.length - 1]);
          } else {
            setSelectedDateForDisplay(null);
            setLeaveRequestsForDate([]);
            setSelectedLeaveRequest(null);
            return;
          }
        } else {
          if (isLeaveDate) {
            dispatch(clearSelectedDates());
            dispatch(toggleSelectedDate(selectedDate));
          } else {
            dispatch(toggleSelectedDate(selectedDate));
          }

          setSelectedDateForDisplay(selectedDate);
        }
      }

      // Always update leave requests for the display date
      const requestsForSelectedDate = (
        calendarState?.leaveRequests || []
      ).filter((req) => {
        const startDate = dayjs(req.start_date);
        const endDate = dayjs(req.end_date);
        return dayjs(selectedDate).isBetween(startDate, endDate, null, "[]");
      });
      setLeaveRequestsForDate(requestsForSelectedDate);
      setSelectedLeaveRequest(
        requestsForSelectedDate.length > 0 ? requestsForSelectedDate[0] : null
      );
    }
  };

  const handleReasonChange = (e) => dispatch(setReason(e.target.value));

  const applyForWFHOrLeave = (type) => {
    const selectedDates = calendarState?.selectedDates || [];
    const reason = calendarState?.reason || "";

    if (!reason.trim()) {
      toast.error("Please enter a reason.");
      return;
    }

    if (selectedDates.length === 0) {
      toast.error("Please select at least one date.");
      return;
    }

    // Sort dates to ensure proper start and end dates
    const sortedDates = [...selectedDates].sort(
      (a, b) => dayjs(a).valueOf() - dayjs(b).valueOf()
    );

    const leaveData = {
      reason: reason.trim(),
      start_date: sortedDates[0],
      end_date: sortedDates[sortedDates.length - 1],
      leave_type: type,
    };

    dispatch(submitLeaveRequest(leaveData))
      .then(() => {
        dispatch(clearSelectedDates());
        dispatch(setReason(""));
        setRangeStart(null);
        setRangeEnd(null);
        toast.success("Leave request submitted successfully!");
      })
      .catch((error) => {
        toast.error("Failed to submit leave request. Please try again.");
        console.error("Leave submission error:", error);
      });
  };

  const approveLeave = (id) => {
    dispatch(approveLeaveRequest(id));
  };

  const admins = useSelector((state) => state.admin.admins);

  const getAdminName = (adminId) => {
    const admin = admins.find((admin) => admin.admin_id === adminId);
    return admin ? admin.username : "Unknown Admin";
  };

  const leaveRequests =
    user?.role === "superadmin"
      ? calendarState?.leaveRequests || []
      : (calendarState?.leaveRequests || []).filter(
          (req) => req.admin === user?.user_id
        );

  const isInLeaveRange = (date) => {
    for (let request of leaveRequests) {
      const startDate = dayjs(request.start_date);
      const endDate = dayjs(request.end_date);
      if (dayjs(date).isBetween(startDate, endDate, null, "[]")) {
        return true;
      }
    }
    return false;
  };

  // Clear range selection when switching modes
  const handleSelectionModeChange = (mode) => {
    setSelectionMode(mode);
    setRangeStart(null);
    setRangeEnd(null);
    if (mode === "single") {
      // Keep current selections when switching to single mode
    } else {
      // Clear selections when switching to range mode
      dispatch(clearSelectedDates());
      setSelectedDateForDisplay(null);
      setLeaveRequestsForDate([]);
      setSelectedLeaveRequest(null);
    }
  };

  const formatDate = (date) => {
    if (!date) return "";
    return dayjs(date).format("DD/MM/YYYY");
  };

  const formatYYYYMMDD = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const currentMonth = new Date(currentMonthObj).toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 lg:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 flex items-center justify-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-teal-400 to-cyan-400 rounded-xl flex items-center justify-center">
              <svg
                className="w-6 h-6 text-slate-900"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            Leave Management Calendar
          </h1>
          <p className="text-slate-400 text-lg">
            Manage your work schedule and leave requests
          </p>
        </div>

        {/* Main Content Container */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Calendar Section */}
          <div className="xl:col-span-2">
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl shadow-2xl border border-slate-700/50 p-6 lg:p-8">
              {/* Calendar Header */}
              <div className="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4">
                <div className="flex items-center gap-4">
                  <button
                    onClick={handlePrevMonth}
                    className="p-2 text-teal-400 hover:text-teal-300 hover:bg-slate-700/50 rounded-lg transition-all duration-200"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white min-w-[200px] text-center">
                    {currentMonth}
                  </h2>
                  <button
                    onClick={handleNextMonth}
                    className="p-2 text-teal-400 hover:text-teal-300 hover:bg-slate-700/50 rounded-lg transition-all duration-200"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>
                <div className="text-teal-400 text-sm bg-slate-700/50 px-4 py-2 rounded-full">
                  {user?.role === "admin" ? "Admin View" : "Super Admin View"}
                </div>
              </div>

              {/* Calendar Grid */}
              <div className="bg-slate-900/80 rounded-xl p-4 lg:p-6 backdrop-blur-sm border border-slate-700/30">
                {/* Weekday Headers */}
                <div className="grid grid-cols-7 gap-2 lg:gap-4 mb-4">
                  {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map(
                    (day) => (
                      <div
                        key={day}
                        className="text-slate-400 text-xs lg:text-sm text-center font-semibold py-2"
                      >
                        {day}
                      </div>
                    )
                  )}
                </div>

                {/* Calendar Days */}
                <div className="grid grid-cols-7 gap-2 lg:gap-4">
                  {/* Empty spaces for first week */}
                  {Array.from({ length: firstDayOfMonth }).map((_, idx) => (
                    <div key={`empty-${idx}`} className="aspect-square" />
                  ))}

                  {/* Days of the month */}
                  {Array.from({ length: daysInMonth }).map((_, idx) => {
                    const day = idx + 1;
                    const currentDate = new Date(currentMonthObj);
                    currentDate.setDate(day);
                    const dateStr = formatYYYYMMDD(currentDate);

                    const isSelected =
                      calendarState?.selectedDates?.includes(dateStr) || false;
                    const hasEvent =
                      calendarState?.events?.some(
                        (event) => event.date === dateStr
                      ) || false;
                    const isLeaveDate = isInLeaveRange(dateStr);
                    const isToday = dayjs().format("YYYY-MM-DD") === dateStr;
                    const isInPreview =
                      selectionMode === "range" &&
                      rangeStart &&
                      !rangeEnd &&
                      isInPreviewRange(dateStr);
                    const isRangeStart = rangeStart === dateStr;
                    const isRangeEnd = rangeEnd === dateStr;

                    return (
                      <div
                        key={day}
                        onClick={() => handleDateSelect(day)}
                        className={`
                          aspect-square flex items-center justify-center
                          rounded-xl cursor-pointer text-sm lg:text-lg font-semibold
                          transition-all duration-300 transform hover:scale-105
                          ${
                            isSelected
                              ? "bg-gradient-to-br from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/25"
                              : isRangeStart && rangeEnd
                              ? "bg-gradient-to-br from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/25 ring-2 ring-teal-300"
                              : isRangeEnd
                              ? "bg-gradient-to-br from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/25 ring-2 ring-teal-300"
                              : isRangeStart
                              ? "bg-gradient-to-br from-teal-400 to-cyan-400 text-white shadow-lg shadow-teal-400/25 ring-2 ring-teal-200"
                              : isInPreview
                              ? "bg-gradient-to-br from-teal-300 to-cyan-300 text-slate-800 shadow-md"
                              : hasEvent
                              ? "bg-gradient-to-br from-teal-600 to-cyan-600 text-white shadow-md"
                              : isLeaveDate
                              ? "bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-md"
                              : isToday
                              ? "bg-slate-700 text-teal-400 border-2 border-teal-400"
                              : "text-slate-300 hover:bg-slate-700/50 hover:text-white"
                          }
                          ${
                            isSelected ||
                            hasEvent ||
                            isLeaveDate ||
                            isInPreview ||
                            isRangeStart ||
                            isRangeEnd
                              ? "shadow-lg"
                              : ""
                          }
                        `}
                      >
                        {day}
                      </div>
                    );
                  })}

                  {/* Empty spaces for last week */}
                  {Array.from({ length: 6 - lastDayOfMonth }).map((_, idx) => (
                    <div key={`empty-end-${idx}`} className="aspect-square" />
                  ))}
                </div>
              </div>

              {/* Legend */}
              <div className="mt-6 flex flex-wrap gap-4 justify-center lg:justify-start">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-gradient-to-br from-teal-500 to-cyan-500 rounded"></div>
                  <span className="text-slate-300 text-sm">Selected</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-gradient-to-br from-teal-300 to-cyan-300 rounded"></div>
                  <span className="text-slate-300 text-sm">Range Preview</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-gradient-to-br from-amber-500 to-orange-500 rounded"></div>
                  <span className="text-slate-300 text-sm">Leave Day</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-slate-700 border-2 border-teal-400 rounded"></div>
                  <span className="text-slate-300 text-sm">Today</span>
                </div>
              </div>

              {/* Admin Controls */}
              {user?.role === "admin" && (
                <div className="mt-8 space-y-4">
                  {/* Selection Mode Toggle */}
                  <div className="flex items-center justify-between p-4 bg-slate-700/30 rounded-xl border border-slate-600/30">
                    <span className="text-white font-medium">
                      Selection Mode:
                    </span>
                    <div className="flex bg-slate-800/50 rounded-lg p-1">
                      <button
                        onClick={() => handleSelectionModeChange("single")}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                          selectionMode === "single"
                            ? "bg-teal-500 text-white shadow-md"
                            : "text-slate-300 hover:text-white hover:bg-slate-700/50"
                        }`}
                      >
                        Single Dates
                      </button>
                      <button
                        onClick={() => handleSelectionModeChange("range")}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                          selectionMode === "range"
                            ? "bg-teal-500 text-white shadow-md"
                            : "text-slate-300 hover:text-white hover:bg-slate-700/50"
                        }`}
                      >
                        Date Range
                      </button>
                    </div>
                  </div>

                  {/* Range Selection Instructions */}
                  {selectionMode === "range" && (
                    <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                      <div className="flex items-center gap-2 text-blue-400 text-sm">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        {!rangeStart
                          ? "Click a date to start selecting your range"
                          : !rangeEnd
                          ? "Click another date to complete your range"
                          : "Range selected! Click a new date to start over"}
                      </div>
                    </div>
                  )}

                  {/* Clear Selection Button */}
                  {(calendarState?.selectedDates?.length > 0 || rangeStart) && (
                    <button
                      onClick={() => {
                        dispatch(clearSelectedDates());
                        setRangeStart(null);
                        setRangeEnd(null);
                        setSelectedDateForDisplay(null);
                        setLeaveRequestsForDate([]);
                        setSelectedLeaveRequest(null);
                      }}
                      className="w-full py-2 px-4 bg-slate-600/50 hover:bg-slate-600 text-white rounded-lg transition-all duration-200 text-sm"
                    >
                      Clear Selection
                    </button>
                  )}

                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Enter reason for leave/WFH..."
                      value={calendarState?.reason || ""}
                      onChange={handleReasonChange}
                      className="w-full p-4 bg-slate-700/50 border border-slate-600/50 rounded-xl
                        text-white placeholder-slate-400 focus:outline-none focus:border-teal-500
                        focus:ring-2 focus:ring-teal-500/20 transition-all duration-200
                        backdrop-blur-sm"
                    />
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <svg
                        className="w-5 h-5 text-slate-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                        />
                      </svg>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <button
                      onClick={() => applyForWFHOrLeave("WFH")}
                      className="py-4 px-6 bg-gradient-to-r from-blue-600 to-blue-700
                        hover:from-blue-700 hover:to-blue-800 text-white rounded-xl
                        transition-all duration-200 font-semibold shadow-lg hover:shadow-xl
                        transform hover:scale-105 flex items-center justify-center gap-2"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2m-6 4h6"
                        />
                      </svg>
                      Apply for WFH
                    </button>
                    <button
                      onClick={() => applyForWFHOrLeave("LEAVE")}
                      className="py-4 px-6 bg-gradient-to-r from-teal-600 to-teal-700
                        hover:from-teal-700 hover:to-teal-800 text-white rounded-xl
                        transition-all duration-200 font-semibold shadow-lg hover:shadow-xl
                        transform hover:scale-105 flex items-center justify-center gap-2"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Apply for Leave
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Leave Requests Panel */}
          <div className="xl:col-span-1">
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl shadow-2xl border border-slate-700/50 p-6 h-fit sticky top-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-gradient-to-r from-teal-400 to-cyan-400 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-slate-900"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white">Leave Details</h3>
              </div>

              {/* Selected Dates Info */}
              {calendarState?.selectedDates &&
                calendarState.selectedDates.length > 0 && (
                  <div className="mt-4 p-4 bg-slate-700/50 rounded-xl border border-slate-600/30">
                    <div className="text-teal-400 font-semibold mb-2 flex items-center gap-2">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                        />
                      </svg>
                      Selected Dates ({calendarState.selectedDates.length})
                      {selectionMode === "range" && rangeStart && rangeEnd && (
                        <span className="text-xs bg-teal-500/20 text-teal-300 px-2 py-1 rounded-full">
                          Range Mode
                        </span>
                      )}
                    </div>
                    <div className="text-white text-sm">
                      {calendarState.selectedDates.length === 1
                        ? formatDate(calendarState.selectedDates[0])
                        : calendarState.selectedDates.length <= 3
                        ? [...calendarState.selectedDates]
                            .sort(
                              (a, b) => dayjs(a).valueOf() - dayjs(b).valueOf()
                            )
                            .map((date) => formatDate(date))
                            .join(", ")
                        : `${formatDate(
                            [...calendarState.selectedDates].sort(
                              (a, b) => dayjs(a).valueOf() - dayjs(b).valueOf()
                            )[0]
                          )} to ${formatDate(
                            [...calendarState.selectedDates].sort(
                              (a, b) => dayjs(a).valueOf() - dayjs(b).valueOf()
                            )[calendarState.selectedDates.length - 1]
                          )}`}
                    </div>
                    {selectionMode === "range" && rangeStart && rangeEnd && (
                      <div className="mt-2 text-xs text-slate-400">
                        {calendarState.selectedDates.length} day
                        {calendarState.selectedDates.length > 1 ? "s" : ""}{" "}
                        selected
                      </div>
                    )}
                  </div>
                )}

              {/* Range Selection Status */}
              {selectionMode === "range" && rangeStart && !rangeEnd && (
                <div className="mt-4 p-4 bg-blue-500/10 rounded-xl border border-blue-500/30">
                  <div className="text-blue-400 font-semibold mb-1 flex items-center gap-2">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Range Start Selected
                  </div>
                  <div className="text-blue-300 text-sm">
                    Start: {formatDate(rangeStart)}
                  </div>
                  <div className="text-blue-300/70 text-xs mt-1">
                    Click another date to complete your range
                  </div>
                </div>
              )}

              {selectedDateForDisplay ? (
                <div className="mb-4 p-4 bg-slate-700/50 rounded-xl border border-slate-600/30">
                  <div className="flex items-center gap-2 text-teal-400 font-semibold">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                    Viewing Details for: {formatDate(selectedDateForDisplay)}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <svg
                    className="w-16 h-16 text-slate-600 mx-auto mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="text-slate-400">
                    Select a date to view leave details
                  </p>
                </div>
              )}

              {Array.isArray(leaveRequestsForDate) &&
              leaveRequestsForDate.length > 0 ? (
                <div className="space-y-4 max-h-[520px] overflow-auto">
                  {leaveRequestsForDate.map((request, index) => (
                    <div
                      key={request?.id || index}
                      className="bg-slate-900/80 rounded-xl p-5 border border-slate-700/50 backdrop-blur-sm"
                    >
                      <div className="space-y-3">
                        {/* Leave Type Badge */}
                        <div className="flex justify-between items-start">
                          <span
                            className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                              request?.leave_type === "WFH"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-orange-100 text-orange-800"
                            }`}
                          >
                            {request?.leave_type || "N/A"}
                          </span>
                          <span
                            className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                              request?.status === "APPROVED"
                                ? "bg-green-100 text-green-800"
                                : request?.status === "PENDING"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {request?.status || "N/A"}
                          </span>
                        </div>

                        {/* Employee Info */}
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                            <span className="text-teal-600 font-semibold text-sm">
                              {getAdminName(request?.admin)
                                .charAt(0)
                                .toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="text-white font-medium">
                              {getAdminName(request?.admin)}
                            </div>
                            <div className="text-slate-400 text-sm">
                              Employee
                            </div>
                          </div>
                        </div>

                        {/* Date Range */}
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <div className="text-slate-400">Start Date</div>
                            <div className="text-white font-medium">
                              {formatDate(request?.start_date)}
                            </div>
                          </div>
                          <div>
                            <div className="text-slate-400">End Date</div>
                            <div className="text-white font-medium">
                              {formatDate(request?.end_date)}
                            </div>
                          </div>
                        </div>

                        {/* Reason */}
                        <div>
                          <div className="text-slate-400 text-sm mb-2">
                            Reason
                          </div>
                          <div className="text-white bg-slate-800/50 p-3 rounded-lg text-sm max-h-20 overflow-y-auto">
                            {request?.reason || "No reason provided"}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : selectedDateForDisplay ? (
                <div className="text-center py-8">
                  <svg
                    className="w-12 h-12 text-slate-600 mx-auto mb-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                  <p className="text-slate-400 text-sm">
                    No leave requests for this date
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CalendarComponent;
